import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { CreditCard, Calendar, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionCard = ({ getAuthHeaders }) => {
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/my`, getAuthHeaders());
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2"></div>
        </div>
      </Card>
    );
  }

  if (!subscription) {
    return null;
  }

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      trialing: 'bg-blue-100 text-blue-700',
      past_due: 'bg-red-100 text-red-700',
      canceled: 'bg-gray-100 text-gray-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getPlanName = (plan) => {
    const names = {
      starter: 'Starter',
      pro: 'Professional',
      pro_plus_sms: 'Pro + SMS'
    };
    return names[plan] || plan;
  };

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <CreditCard className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold">Subscription</h3>
            <p className="text-sm text-gray-500">Plan & billing information</p>
          </div>
        </div>
        <Badge className={getStatusBadge(subscription.status)} data-testid="subscription-status">
          {subscription.status}
        </Badge>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-600">Plan</span>
          <span className="font-bold" data-testid="subscription-plan">{getPlanName(subscription.plan)}</span>
        </div>

        {subscription.trial_ends_at && subscription.status === 'trialing' && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Calendar className="w-4 h-4 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm text-blue-900">
                Trial ends: {new Date(subscription.trial_ends_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        )}

        {subscription.status === 'past_due' && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <p className="text-sm text-red-900">
              Payment required. Please update your payment method.
            </p>
          </div>
        )}

        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-600">Billing Email</span>
          <span className="text-sm font-medium" data-testid="billing-email">{subscription.billing_email}</span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Stripe integration coming soon. Manage billing and upgrade your plan.
        </p>
      </div>
    </Card>
  );
};

export default SubscriptionCard;
